import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { type TransactionFeesSettings } from '~entities/payment/api/fetch-transaction-fees-settings';
import { useToggleTransactionFees } from '~entities/payment/api/toggle-transaction-fee-settings';

import { ExcludeParentDialog } from '~features/payment/ui/exclude-parent-dialog';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

interface ExcludedParent {
  parentId: string;
  parentName: string;
  studentsNames: Array<string>;
}

const columnHelper = createColumnHelper<ExcludedParent>();

interface RemoveParentButtonProps {
  parent: ExcludedParent;
  transactionFeesSettings: TransactionFeesSettings | undefined;
}

const RemoveParentButton = ({
  parent,
  transactionFeesSettings,
}: RemoveParentButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const { mutateAsync: toggleTransactionFees, isPending } =
    useToggleTransactionFees();

  const handleRemove = async () => {
    if (!transactionFeesSettings) return;

    try {
      await toggleTransactionFees({
        passTransactionFeesToCustomers:
          transactionFeesSettings.passTransactionFeesToCustomers,
        cardTransactionFees:
          transactionFeesSettings.cardTransactionFees ?? '2.9',
        cardFixedFee: transactionFeesSettings.cardFixedFee ?? '0.30',
        achTransactionFees: transactionFeesSettings.achTransactionFees ?? '0.8',
        updateInvoices: transactionFeesSettings.updateInvoices,
        excludedParentIds: transactionFeesSettings.excludedParentIds
          .filter((p) => p.parentId !== parent.parentId)
          .map((p) => p.parentId),
      });
      toast.success('Parent removed from exclusion list successfully');
      setShowDialog(false);
    } catch (error) {
      console.error('Error removing parent from exclusion list:', error);
      toast.error('Failed to remove parent from exclusion list');
    }
  };

  return (
    <>
      <Button
        variant='destructive'
        className='size-6 p-0'
        onClick={() => {
          setShowDialog(true);
        }}
      >
        {isPending ? (
          <Loader2 className='size-4 animate-spin' />
        ) : (
          <Trash2 className='size-4' />
        )}
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove Parent from Exclusion List
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <span className='font-semibold'>{parent.parentName}</span> from
              the exclusion list? This parent will be charged transaction fees
              on future payments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <Button onClick={handleRemove} disabled={isPending}>
              {isPending && <Loader2 className='animate-spin' />}
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const createColumns = (
  transactionFeesSettings: TransactionFeesSettings | undefined,
) => [
  columnHelper.accessor('parentName', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Parent Name'
        className='w-40 pl-4 text-black'
        disableSorting
        disableHiding
      />
    ),
    cell: (info) => {
      return (
        <div className='px-4'>
          <div className='text-sm font-medium capitalize text-gray-900'>
            {info.getValue()}
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('studentsNames', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Students'
        className='pl-4 text-black'
        disableSorting
        disableHiding
      />
    ),
    cell: (info) => {
      const students = info.getValue();
      return (
        <div className='px-4'>
          <div className='text-sm text-gray-500'>
            {students.length > 0
              ? students.map((student) => capitalizeWords(student)).join(', ')
              : '-'}
          </div>
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Actions'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      return (
        <div className='flex items-center px-4'>
          <RemoveParentButton
            parent={info.row.original}
            transactionFeesSettings={transactionFeesSettings}
          />
        </div>
      );
    },
  }),
];

export interface ExcludedParentsTableProps {
  transactionFeesSettings: TransactionFeesSettings | undefined;
  isFetching: boolean;
}

export const ExcludedParentsTable = ({
  transactionFeesSettings,
  isFetching,
}: ExcludedParentsTableProps) => {
  const tableData = transactionFeesSettings?.excludedParentIds ?? [];
  const columns = useMemo(
    () => createColumns(transactionFeesSettings),
    [transactionFeesSettings],
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  return (
    <Card>
      <CardHeader>
        <CardTitle>Excluded Parents</CardTitle>
        <CardDescription>
          Manage the parents who will not be charged transaction fees.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <EnrollioTable
          ariaLabel='Excluded parents'
          totalLabel='Parents'
          filtersLayout='inline'
          filterHeader={(table) => (
            <EnrollioTableSearch
              placeholder='Search parents...'
              value={
                (table.getColumn('parentName')?.getFilterValue() as
                  | string
                  | undefined) ?? ''
              }
              onChange={(value) => {
                table.getColumn('parentName')?.setFilterValue(value);
              }}
            />
          )}
          toolbar={(table) => (
            <>
              <ExcludeParentDialog
                transactionFeesSettings={transactionFeesSettings}
                isFetching={isFetching}
              />
              <EnrollioTableColumnToggle table={table} />
            </>
          )}
          columns={columns}
          data={tableData}
          isUpdating={isFetching}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
        />
      </CardContent>
    </Card>
  );
};
